export function trimText(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\s+/g, ' ').trim();
}

export function normalizeKey(value) {
  return trimText(value).toLowerCase();
}

export function normalizeState(value) {
  return trimText(value).toUpperCase();
}

export function normalizeTeamMember(value) {
  const text = trimText(value);
  if (!text) return '';

  const names = text
    .split('&')
    .map((name) => trimText(name))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

  return names.join(' & ');
}

const SERVICE_TOKEN_CANONICAL = {
  design: 'Design',
  ee: 'EE',
  electrical: 'EE',
  pe: 'PE',
  permit: 'Permit',
  permitting: 'Permit',
  labelsandplacards: 'LabelsAndPlacards',
  labels: 'LabelsAndPlacards',
  placards: 'LabelsAndPlacards',
  all: 'All',
};

const SERVICE_ORDER = ['Design', 'EE', 'PE', 'LabelsAndPlacards', 'Permit', 'All'];

function normalizeServiceToken(token) {
  const compact = trimText(token).replace(/[\s_-]+/g, '').toLowerCase();
  return SERVICE_TOKEN_CANONICAL[compact] || trimText(token);
}

export function normalizeServiceType(value) {
  const text = trimText(value);
  if (!text) return '';

  const tokens = text
    .replace(/\+/g, '&')
    .replace(/\s+and\s+/gi, '&')
    .replace(/[\/,;]+/g, '&')
    .split('&')
    .map(normalizeServiceToken)
    .filter(Boolean);

  const unique = [...new Set(tokens)];
  unique.sort((a, b) => {
    const aIndex = SERVICE_ORDER.indexOf(a);
    const bIndex = SERVICE_ORDER.indexOf(b);
    if (aIndex !== -1 || bIndex !== -1) {
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    }
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  });

  return unique.join(' & ');
}


export function normalizeQaResult(value) {
  const text = trimText(value);
  if (!text) return '';
  const compact = text.replace(/[\s_-]+/g, '').toLowerCase();
  if (compact === 'noerror') return 'No Error';
  return text;
}

export function normalizeReason(value, fallback = '(Blank)') {
  const text = trimText(value);
  return text || fallback;
}

export function toNullableText(value) {
  const text = trimText(value);
  return text || null;
}

export function toNullableNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const cleaned = String(value).replace(/,/g, '').trim();
  if (!cleaned) return null;
  const number = Number(cleaned);
  return Number.isFinite(number) ? number : null;
}

function excelSerialToDate(serial) {
  // Excel incorrectly treats 1900 as a leap year. The 25569 offset aligns Excel serials to Unix epoch.
  const utcDays = Math.floor(serial - 25569);
  const utcValue = utcDays * 86400;
  const fractionalDay = serial - Math.floor(serial) + 0.0000001;
  let totalSeconds = Math.floor(86400 * fractionalDay);
  const seconds = totalSeconds % 60;
  totalSeconds -= seconds;
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor(totalSeconds / 60) % 60;
  return new Date(Date.UTC(1970, 0, 1, hours, minutes, seconds) + utcValue * 1000);
}

export function parseDateValue(value) {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  if (typeof value === 'number' && Number.isFinite(value)) {
    if (value > 20000 && value < 80000) return excelSerialToDate(value);
    return null;
  }

  const text = trimText(value).replace(/\r?\n/g, ' ');
  if (!text) return null;

  // Common Salesforce report format: 5/1/2026, 12:59 AM
  const parsed = new Date(text);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  // Try yyyy-mm-dd prefix from long text descriptions.
  const match = text.match(/(\d{4}-\d{2}-\d{2})(?:[ T](\d{2}:\d{2}:\d{2}))?/);
  if (match) {
    const fallback = new Date(match[0].replace(' ', 'T'));
    if (!Number.isNaN(fallback.getTime())) return fallback;
  }

  return null;
}

export function toIsoDateTime(value) {
  const date = parseDateValue(value);
  return date ? date.toISOString() : null;
}

export function toDateOnly(value) {
  const date = parseDateValue(value);
  if (!date) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function hashString(value) {
  let hash = 0;
  const text = String(value || '');
  for (let i = 0; i < text.length; i += 1) {
    hash = (hash << 5) - hash + text.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}
